/**
 * Theme Context
 * Manages light/dark/system theme preference with persistence
 * Uses Tailwind's dark mode class strategy for shadcn/ui
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../auth';
import type { ThemeMode } from '../auth/types';

export type { ThemeMode };
type ResolvedTheme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'panluma_theme_mode';

interface ThemeContextType {
  themeMode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  try {
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    return mediaQuery?.matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') {
    return getSystemTheme();
  }
  return mode;
}

function applyThemeToDocument(theme: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);
}

export function ThemeContextProvider({ children }: { children: React.ReactNode }) {
  const { user, updateUser } = useAuth();

  // Initialize from user preference, localStorage, or default to 'system'
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    // Check user preferences first
    const userPref = user?.preferences?.theme_mode as ThemeMode | undefined;
    if (userPref && ['light', 'dark', 'system'].includes(userPref)) {
      return userPref;
    }
    // Fall back to localStorage
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      if (stored && ['light', 'dark', 'system'].includes(stored)) {
        return stored as ThemeMode;
      }
    }
    return 'system';
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(themeMode));

  // Apply theme to document when resolved theme changes
  useEffect(() => {
    applyThemeToDocument(resolvedTheme);
  }, [resolvedTheme]);

  // Listen for system theme changes when mode is 'system'
  useEffect(() => {
    if (themeMode !== 'system') {
      setResolvedTheme(themeMode);
      return;
    }

    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mediaQuery) {
      setResolvedTheme('light');
      return;
    }
    const handleChange = (e: MediaQueryListEvent) => {
      setResolvedTheme(e.matches ? 'dark' : 'light');
    };

    setResolvedTheme(mediaQuery.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themeMode]);

  // Sync with user preferences when user changes
  useEffect(() => {
    const userPref = user?.preferences?.theme_mode as ThemeMode | undefined;
    if (userPref && ['light', 'dark', 'system'].includes(userPref) && userPref !== themeMode) {
      setThemeModeState(userPref);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- themeMode intentionally excluded to avoid infinite loop
  }, [user?.preferences?.theme_mode]);

  const setThemeMode = useCallback(async (mode: ThemeMode) => {
    setThemeModeState(mode);
    localStorage.setItem(THEME_STORAGE_KEY, mode);

    // Persist to backend if user is authenticated
    if (user) {
      try {
        await updateUser({
          preferences: {
            ...user.preferences,
            theme_mode: mode,
          },
        });
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  }, [user, updateUser]);

  const value = useMemo(() => ({
    themeMode,
    resolvedTheme,
    setThemeMode,
  }), [themeMode, resolvedTheme, setThemeMode]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useThemeMode(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeMode must be used within a ThemeContextProvider');
  }
  return context;
}
